// B.1/B.2 — ONE rule: hue = location, style = status.
//
// Nine hues are needed today (ProResults 9, UTN 9, Powers 3). Each entry
// carries a light tint for block backgrounds, the full hue for the solid
// left border and the legend dot, and a dark neutral text colour. Contrast
// was COMPUTED, not eyeballed — `backend/tests/_tint_contrast.py` parses
// THIS file and the resulting table lives in
// memory/NETWORK_PORTAL_SCHEDULING_C_RECEIPTS.md §2a. Thresholds:
// text-on-tint 4.5:1 (AA normal text), border-on-white 3:1 (AA non-text).
//
// Hue KEYS deliberately avoid the four reserved status names
// (amber/orange/emerald/indigo — `components/locationColors.js`) so a
// location hue can never be read as a status at a glance.
//
// Status is NEVER carried by hue (B.2). It is carried by shape: a check
// icon, a chip, a dashed border, or the existing hatch for WS Reserved.
export const LOCATION_TINTS = [
  { key: "iris",     tint: "#EEF0FB", border: "#4F5BD5", text: "#1F2340" },
  { key: "pine",     tint: "#E7F4F2", border: "#0F766E", text: "#12312E" },
  { key: "ochre",    tint: "#FBF3E4", border: "#B45309", text: "#3A2708" },
  { key: "cerise",   tint: "#FBEDF0", border: "#BE123C", text: "#3F0A18" },
  { key: "plum",     tint: "#F3EDFB", border: "#6D28D9", text: "#2A1247" },
  { key: "azure",    tint: "#E8F2FB", border: "#0369A1", text: "#0C2A3D" },
  { key: "moss",     tint: "#EFF5E4", border: "#4D7C0F", text: "#1E2F09" },
  { key: "graphite", tint: "#EDF0F3", border: "#334155", text: "#1B222B" },
  { key: "copper",   tint: "#FAEEE8", border: "#9A3412", text: "#33150A" },
];

// Assignment by SORTED RANK of the login's location codes — the same
// mechanism `components/locationColors.js::assignLocationColors` uses, so
// the chip colour and the tint share one rank and one truth.
//
// HOTFIX 2026-09-18: this used to be `tintFor(code)`, a polynomial hash
// modulo nine. Nine codes into nine buckets collides by pigeonhole, and
// pro-pt's real codes (PB000, PC000, PD000, PE000, PL000, PM000, PP000,
// PS000, PT000) are the worst case for a hash — nine strings differing in
// ONE character at the SAME position — so three of nine sites rendered the
// same hue in production. Sorted rank is immune by construction: distinct
// codes get distinct ranks, so ≤9 locations always get ≤9 distinct tints.
// The hash is DELETED, not deprecated.
export function assignLocationTints(codes) {
  const sorted = [...new Set((codes || []).filter(Boolean))].sort();
  const map = {};
  sorted.forEach((code, i) => {
    map[code] = {
      tint: LOCATION_TINTS[i % LOCATION_TINTS.length],
      // Beyond nine, the hue wraps and the LABEL carries a numbered
      // suffix — hue is never the only signal (the palette's rule).
      suffix: i >= LOCATION_TINTS.length
        ? ` (${Math.floor(i / LOCATION_TINTS.length) + 1})`
        : "",
    };
  });
  return map;
}

// The ONE derivation every clinic surface uses: the map comes from the
// login's own location set, so the calendar, the month chips, the legend,
// the table, the sidebar and the modal header cannot disagree.
export function tintMapFor(profile) {
  return assignLocationTints((profile?.locations || []).map((l) => l.location_code));
}

export function tintOf(tintMap, locationCode) {
  return tintMap?.[locationCode]?.tint || null;
}

// B.4 — the ONE label source on the client. The server now sends the
// roster `name` as `short_label` (clinic_portal_router.py:98-121, with the
// append-the-code collision fallback); anything that still reaches for
// `city` is a bug. Never compose a label from city here.
export function locationLabel(profile, locationCode) {
  const meta = (profile?.locations || []).find(
    (l) => l.location_code === locationCode);
  return meta?.short_label || meta?.location_name || locationCode || "";
}

// B.2 — the ONE status classifier for calendar blocks. Shape, never hue.
export function statusKeyFor(request, time) {
  if (request?.type === "direct_booking" || request?.status === "direct_booking") return "direct";
  if (request?.status === "locked_in" && time?.is_confirmed) return "confirmed";
  if (request?.status === "suggested_alternative") return "alt_sent";
  if (request?.status === "cancelled") return "cancelled";
  return "pending";
}

// Style tokens for a calendar block: location tint underneath, status
// expressed as shape on top (B.2). Takes the TINT (from the shared
// assignment map) — never a location code, so no hue can be derived
// locally.
export function blockStyle(tint, status) {
  const t = tint || null;
  const dashed = status === "pending" || status === "alt_sent";
  const line = t ? t.border : "#94A3B8";
  return {
    background: t ? t.tint : "#F8FAFC",
    color: t ? t.text : "#1B222B",
    borderLeft: `4px solid ${line}`,
    borderTop: dashed ? `1px dashed ${line}` : "1px solid transparent",
    borderRight: dashed ? `1px dashed ${line}` : "1px solid transparent",
    borderBottom: dashed ? `1px dashed ${line}` : "1px solid transparent",
  };
}

export const STATUS_SHAPES = [
  { status: "confirmed", label: "Confirmed", shape: "check" },
  { status: "direct", label: "Direct", shape: "chip" },
  { status: "pending", label: "Proposed", shape: "dashed" },
  { status: "alt_sent", label: "Alt sent", shape: "dashed" },
  { status: "reserved_open", label: "WS Reserved (open)", shape: "hatch" },
  { status: "reserved_blocked", label: "WS Reserved (blocked)", shape: "stripe" },
];
